import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, downloadFile } from '../../utils/api';
import api from '../../utils/api';
import { formatUTCTimeToIST } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { 
  LayoutDashboard, FileText, Database, LogOut, 
  MapPin, CheckCircle2, XCircle, AlertCircle, Users, X, Download, Clock, RefreshCw, Activity,
  HardDrive, Settings, IndianRupee, AlertTriangle, ClipboardList, Zap
} from 'lucide-react';

const AdminDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalData, setModalData] = useState([]);
  const [modalType, setModalType] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [istTime, setIstTime] = useState('');
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [modalDate, setModalDate] = useState(new Date().toISOString().split('T')[0]);
  const [modalLoading, setModalLoading] = useState(false);
  const { user, logout, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchSummary();
    fetchServerTime();
    fetchPendingApprovals();
    // No auto-refresh - user must click refresh button
  }, [user, userType, navigate, authLoading]);

  const fetchPendingApprovals = async () => {
    try {
      const response = await adminAPI.getCorrectionPermissionRequests();
      setPendingApprovals(response.data.total || 0);
    } catch (error) {
      console.log('Could not fetch pending approvals');
    }
  };

  const fetchServerTime = async () => {
    try {
      const response = await api.get('/server-time');
      setIstTime(response.data.ist_time);
    } catch (error) {
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const ist = new Date(now.getTime() + istOffset);
      setIstTime(ist.toISOString().slice(0, 19).replace('T', ' '));
    }
  };

  const fetchSummary = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await adminAPI.getDashboardSummary();
      setSummary(response.data);
      setLastUpdated(new Date());
      if (isManualRefresh) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchSummary(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Sort parks alphabetically by name
  const sortByName = (arr) => {
    return [...arr].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  const showBreakdown = async (type, dateVal) => {
    const queryDate = dateVal || new Date().toISOString().split('T')[0];
    setModalType(type);
    setModalDate(queryDate);
    setModalLoading(true);
    try {
      if (type === 'total_parks') {
        setModalTitle('All Parks');
        const response = await adminAPI.getParks();
        const parks = response.data.parks.map(p => ({
          id: p.park_id,
          name: p.park_name,
          zone: p.zone,
          division: p.division,
          supervisor: p.assigned_inspector_id || 'Unassigned',
          manpower: p.manpower_list?.length || 0
        }));
        setModalData(sortByName(parks));
      } else if (type === 'visited_today') {
        setModalTitle(`Parks Visited (${queryDate})`);
        const response = await adminAPI.getCleanlinessReport(queryDate);
        const parks = response.data.cleanliness_report?.map(p => ({
          id: p.park_id,
          name: p.park_name,
          zone: p.zone,
          inspector: p.inspector_name,
          cleanliness: p.cleanliness,
          time: formatUTCTimeToIST(p.end_time)
        })) || [];
        setModalData(sortByName(parks));
      } else if (type === 'inspectors') {
        setModalTitle('All Supervisors');
        const response = await adminAPI.getInspectors();
        const parksResponse = await adminAPI.getParks();
        const parks = parksResponse.data.parks;
        
        // Get today's logins to determine active supervisors
        const monitoringResponse = await adminAPI.getSupervisorMonitoring(new Date().toISOString().split('T')[0]);
        const activeSupervisors = new Set(
          monitoringResponse.data.supervisors
            .filter(s => s.login_time !== '-')
            .map(s => s.supervisor_id)
        );
        
        setModalData(response.data.inspectors.map(i => ({
          id: i.supervisor_id,
          name: i.supervisor_name,
          status: activeSupervisors.has(i.supervisor_id) ? 'Active Today' : 'Not Active',
          assigned: parks.filter(p => p.assigned_inspector_id === i.supervisor_id).length
        })));
      } else if (type === 'closed_parks') {
        setModalTitle(`Closed Parks (${queryDate})`);
        const response = await adminAPI.getClosedParks(queryDate);
        const parks = response.data.closed_parks?.map(p => ({
          id: p.park_id,
          name: p.park_name,
          zone: p.zone,
          inspector: p.inspector_name,
          remarks: p.remarks || '-'
        })) || [];
        setModalData(sortByName(parks));
      } else if (type === 'poor_cleanliness') {
        setModalTitle(`Poor Cleanliness Parks (${queryDate})`);
        const response = await adminAPI.getCleanlinessReport(queryDate);
        const parks = response.data.cleanliness_report?.filter(p => p.cleanliness === 'Poor').map(p => ({
          id: p.park_id,
          name: p.park_name,
          zone: p.zone,
          inspector: p.inspector_name,
          remarks: p.remarks || '-'
        })) || [];
        setModalData(sortByName(parks));
      } else if (type === 'pending') {
        setModalTitle(`Parks Pending (${queryDate})`);
        const parksResponse = await adminAPI.getParks();
        const cleanlinessResponse = await adminAPI.getCleanlinessReport(queryDate);
        const visitedIds = new Set(cleanlinessResponse.data.cleanliness_report?.map(p => p.park_id) || []);
        const closedResponse = await adminAPI.getClosedParks(queryDate);
        const closedIds = new Set(closedResponse.data.closed_parks?.map(p => p.park_id) || []);
        
        const pendingParks = parksResponse.data.parks
          .filter(p => !visitedIds.has(p.park_id) && !closedIds.has(p.park_id))
          .map(p => ({
            id: p.park_id,
            name: p.park_name,
            zone: p.zone,
            division: p.division,
            supervisor: p.assigned_inspector_id || 'Unassigned'
          }));
        setModalData(sortByName(pendingParks));
      } else if (type === 'no_manpower') {
        setModalTitle(`Parks with No Manpower (${queryDate})`);
        const response = await adminAPI.getNoManpowerParks(queryDate);
        const parks = response.data.parks?.map(p => ({
          id: p.park_id,
          name: p.park_name,
          zone: p.zone,
          division: p.division,
          supervisor: p.inspector_name || 'Unknown',
          visit_time: p.visit_time || '-',
          cleanliness: p.cleanliness || '-'
        })) || [];
        setModalData(sortByName(parks));
      }
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load details');
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalDateChange = async (e) => {
    const newDate = e.target.value;
    setModalDate(newDate);
    await showBreakdown(modalType, newDate);
  };

  const downloadExcel = () => {
    if (!modalData.length) return;
    
    // Create CSV content
    const headers = Object.keys(modalData[0]).join(',');
    const rows = modalData.map(row => 
      Object.values(row).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const csvContent = `${headers}\n${rows}`;
    
    // Download as CSV (Excel compatible)
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const cleanTitle = modalTitle.replace(/\s*\([^)]*\)/g, '').replace(/\s+/g, '_');
    link.download = `${cleanTitle}_${modalDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Downloaded successfully');
  };

  // Pending = (total parks - renovation) - visited today
  const expectedParks = (summary?.total_parks || 0) - (summary?.renovation_parks || 0);
  const pendingCount = expectedParks - (summary?.visited_today || 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-sm opacity-90">Park Monitoring System</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-10 px-4 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handleLogout}
              className="h-10 px-4 rounded-lg bg-white/20 hover:bg-white/30 flex items-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* IST Time and Last Updated */}
        {!loading && (
          <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="font-semibold">IST:</span> {istTime}
            </div>
            {lastUpdated && (
              <div>Last updated: {lastUpdated.toLocaleTimeString()}</div>
            )}
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-slate-600">Loading dashboard...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <button
                onClick={() => showBreakdown('total_parks')}
                className="bg-white border-l-4 border-l-blue-600 shadow-sm rounded-r-lg p-4 text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                      Total Parks
                      {summary?.renovation_parks > 0 && (
                        <span className="text-amber-500 font-normal ml-1">
                          ({summary.renovation_parks} renovation)
                        </span>
                      )}
                    </div>
                    <div className="text-3xl font-bold text-blue-600">{summary?.total_parks || 0}</div>
                  </div>
                  <MapPin className="w-8 h-8 text-blue-600 opacity-30" />
                </div>
              </button>

              <button
                onClick={() => showBreakdown('visited_today')}
                className="bg-white border-l-4 border-l-green-600 shadow-sm rounded-r-lg p-4 text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                      Visited Today
                    </div>
                    <div className="text-3xl font-bold text-green-600">{summary?.visited_today || 0}</div>
                    <div className="text-xs text-slate-500">{summary?.completion_percentage?.toFixed(0)}%</div>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-600 opacity-30" />
                </div>
              </button>

              <button
                onClick={() => showBreakdown('pending')}
                className="bg-white border-l-4 border-l-yellow-500 shadow-sm rounded-r-lg p-4 text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                      Pending
                    </div>
                    <div className="text-3xl font-bold text-yellow-600">{pendingCount > 0 ? pendingCount : 0}</div>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500 opacity-30" />
                </div>
              </button>

              <button
                onClick={() => showBreakdown('inspectors')}
                className="bg-white border-l-4 border-l-purple-600 shadow-sm rounded-r-lg p-4 text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                      Total Supervisors
                    </div>
                    <div className="text-3xl font-bold text-purple-600">{summary?.total_supervisors || 0}</div>
                    <div className="text-xs text-green-600 mt-1">
                      {summary?.active_supervisors_today || 0} active today
                    </div>
                  </div>
                  <Users className="w-8 h-8 text-purple-600 opacity-30" />
                </div>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <button
                onClick={() => showBreakdown('closed_parks')}
                className="bg-red-50 border-l-4 border-l-red-500 rounded-r-lg p-4 text-left hover:bg-red-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-red-600" />
                  <div>
                    <div className="text-2xl font-bold text-red-600">{summary?.closed_parks || 0}</div>
                    <div className="text-xs font-semibold text-red-800">Closed Parks</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => showBreakdown('no_manpower')}
                className="bg-orange-50 border-l-4 border-l-orange-500 rounded-r-lg p-4 text-left hover:bg-orange-100 transition-colors"
                data-testid="no-manpower-card"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-8 h-8 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold text-orange-600">{summary?.open_no_manpower || 0}</div>
                    <div className="text-xs font-semibold text-orange-800">No Manpower</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => showBreakdown('poor_cleanliness')}
                className="bg-red-50 border-l-4 border-l-red-500 rounded-r-lg p-4 text-left hover:bg-red-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                  <div>
                    <div className="text-2xl font-bold text-red-600">{summary?.poor_cleanliness || 0}</div>
                    <div className="text-xs font-semibold text-red-800">Poor Cleanliness</div>
                  </div>
                </div>
              </button>
            </div>

            <h2 className="text-xl font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Quick Actions
            </h2>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/admin/reports"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-600 transition-colors"
              >
                <FileText className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="font-bold mb-1">View Reports</h3>
                <p className="text-xs text-slate-600">Attendance & cleanliness</p>
              </Link>

              <Link
                to="/admin/calendar"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-600 transition-colors"
              >
                <LayoutDashboard className="w-8 h-8 text-green-600 mb-2" />
                <h3 className="font-bold mb-1">Attendance Calendar</h3>
                <p className="text-xs text-slate-600">Monthly view</p>
              </Link>

              <Link
                to="/admin/data-management"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-600 transition-colors"
              >
                <Database className="w-8 h-8 text-purple-600 mb-2" />
                <h3 className="font-bold mb-1">Data Management</h3>
                <p className="text-xs text-slate-600">Parks & employees</p>
              </Link>

              <Link
                to="/admin/bulk-upload"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-600 transition-colors"
              >
                <FileText className="w-8 h-8 text-orange-600 mb-2" />
                <h3 className="font-bold mb-1">Excel Import</h3>
                <p className="text-xs text-slate-600">Bulk upload data</p>
              </Link>

              <Link
                to="/admin/supervisor-monitoring"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-600 transition-colors"
                data-testid="supervisor-monitoring-link"
              >
                <Activity className="w-8 h-8 text-indigo-600 mb-2" />
                <h3 className="font-bold mb-1">Supervisor Monitoring</h3>
                <p className="text-xs text-slate-600">Track performance</p>
              </Link>

              <Link
                to="/admin/storage"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-600 transition-colors"
                data-testid="storage-dashboard-link"
              >
                <HardDrive className="w-8 h-8 text-purple-600 mb-2" />
                <h3 className="font-bold mb-1">Storage Dashboard</h3>
                <p className="text-xs text-slate-600">Manage photos</p>
              </Link>

              <Link
                to="/admin/settings"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-blue-600 transition-colors relative"
                data-testid="settings-link"
              >
                {pendingApprovals > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {pendingApprovals}
                  </span>
                )}
                <Settings className="w-8 h-8 text-slate-600 mb-2" />
                <h3 className="font-bold mb-1">Settings</h3>
                <p className="text-xs text-slate-600">
                  {pendingApprovals > 0 ? `${pendingApprovals} pending approval(s)` : 'GPS & configuration'}
                </p>
              </Link>

              <Link
                to="/admin/monthly-reports"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-indigo-600 transition-colors"
                data-testid="monthly-reports-link"
              >
                <IndianRupee className="w-8 h-8 text-indigo-600 mb-2" />
                <h3 className="font-bold mb-1">Monthly Reports</h3>
                <p className="text-xs text-slate-600">Zone salary reports</p>
              </Link>

              <Link
                to="/admin/pending-attendance"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-orange-600 transition-colors"
                data-testid="pending-attendance-link"
              >
                <AlertTriangle className="w-8 h-8 text-orange-600 mb-2" />
                <h3 className="font-bold mb-1">Pending Attendance</h3>
                <p className="text-xs text-slate-600">Not visited parks</p>
              </Link>

              <Link
                to="/admin/calendar?tab=pending-worker"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-amber-600 transition-colors"
                data-testid="pending-worker-attendance-link"
              >
                <AlertCircle className="w-8 h-8 text-amber-600 mb-2" />
                <h3 className="font-bold mb-1">Pending Worker Attendance</h3>
                <p className="text-xs text-slate-600">Supervisor calendar format</p>
              </Link>

              <Link
                to="/admin/overtime"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-orange-600 transition-colors"
                data-testid="overtime-link"
              >
                <Clock className="w-8 h-8 text-orange-600 mb-2" />
                <h3 className="font-bold mb-1">Overtime</h3>
                <p className="text-xs text-slate-600">Assign overtime parks</p>
              </Link>

              <Link
                to="/admin/questionnaires"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-purple-600 transition-colors"
                data-testid="questionnaire-link"
              >
                <ClipboardList className="w-8 h-8 text-purple-600 mb-2" />
                <h3 className="font-bold mb-1">Questionnaires</h3>
                <p className="text-xs text-slate-600">Dynamic surveys</p>
              </Link>

              <Link
                to="/admin/zone-staff"
                className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-yellow-600 transition-colors"
                data-testid="zone-staff-link"
              >
                <Zap className="w-8 h-8 text-yellow-600 mb-2" />
                <h3 className="font-bold mb-1">Zone Staff</h3>
                <p className="text-xs text-slate-600">Electricians & Supervisors</p>
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Breakdown Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="bg-slate-800 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">{modalTitle}</h3>
                <p className="text-sm opacity-90">{modalData.length} records</p>
              </div>
              <div className="flex items-center gap-2">
                {['visited_today', 'closed_parks', 'poor_cleanliness', 'pending', 'no_manpower'].includes(modalType) && (
                  <input
                    type="date"
                    value={modalDate}
                    onChange={handleModalDateChange}
                    className="h-10 px-3 text-slate-900 bg-white border border-slate-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="modal-date-filter"
                  />
                )}
                <button 
                  onClick={downloadExcel}
                  className="h-10 px-4 bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-sm flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Excel
                </button>
                <button 
                  onClick={() => {
                    setShowModal(false);
                    setModalDate(new Date().toISOString().split('T')[0]);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="overflow-auto max-h-[65vh] relative">
              {modalLoading && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10" data-testid="modal-loading-overlay">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {modalData.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No data available
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      {Object.keys(modalData[0]).map(key => (
                        <th key={key} className="text-left p-3 font-bold text-sm uppercase text-slate-600">
                          {key.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.map((row, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        {Object.entries(row).map(([key, value], vIdx) => (
                          <td key={vIdx} className="p-3 text-sm">
                            {key === 'status' ? (
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                value === 'Active Today' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {value}
                              </span>
                            ) : value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
